/**
 * ABSOLUTELY COMPLETE ACCOUNT DASHBOARD
 * NO SHORTCUTS - EVERY SINGLE FUNCTION INCLUDED
 */

class AccountDashboard {
  constructor() {
    this.customerId = window.customerId || window.customer?.id;
    if (!this.customerId) {
      console.log('❌ No customer ID found');
      return;
    }
    
    this.customerData = this.loadCustomerData();
    this.horses = [];
    this.wishlists = {};
    this.products = {};
    this.allProducts = {};
    
    console.log('✓ Dashboard initialized for customer:', this.customerId);
    this.init();
  }

  async init() {
    this.loadWishlistData();
    await this.loadProducts();
    
    if (document.querySelector('.customer.account, main[role="main"]')) {
      this.transformAccountPage();
      this.loadDynamicContent();
      this.setupInteractions();
    }
    
    this.initActivityTracking();
    this.updateHeaderAvatar();
  }

  loadCustomerData() {
    const stored = localStorage.getItem(`customer_profile_${this.customerId}`);
    const data = stored ? JSON.parse(stored) : {
      avatar: null,
      profile: {},
      preferences: {
        marketing: true,
        orderUpdates: true
      },
      joinDate: new Date().toISOString()
    };
    
    console.log('Loaded customer data:', data);
    return data;
  }

  saveCustomerData() {
    localStorage.setItem(`customer_profile_${this.customerId}`, JSON.stringify(this.customerData));
    console.log('✓ Saved customer data');
  }

  loadWishlistData() {
    this.horses = JSON.parse(localStorage.getItem(`horses_${this.customerId}`)) || [];
    this.wishlists = JSON.parse(localStorage.getItem(`wishlists_${this.customerId}`)) || {};
    console.log('✓ Loaded wishlist data:', this.horses.length, 'horses');
  }

  saveWishlistData() {
    localStorage.setItem(`horses_${this.customerId}`, JSON.stringify(this.horses));
    localStorage.setItem(`wishlists_${this.customerId}`, JSON.stringify(this.wishlists));
  }

  async loadProducts() {
    const handles = [...new Set(Object.values(this.wishlists).flat())];
    console.log('Loading products for', handles.length, 'handles');
    
    for (const handle of handles.slice(0, 20)) {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (response.ok) {
          const product = await response.json();
          this.products[handle] = product;
          this.allProducts[handle] = product;
          console.log('✓ Loaded product:', product.title);
        }
      } catch (e) {
        console.error('Failed to load product:', handle, e);
      }
    }
    
    console.log('✓ Total products loaded:', Object.keys(this.products).length);
  }

  getCustomerFirstName() {
    // PRIORITY 1: Check saved profile
    if (this.customerData.profile && this.customerData.profile.firstName) {
      console.log('Using saved profile name:', this.customerData.profile.firstName);
      return this.customerData.profile.firstName;
    }
    
    // PRIORITY 2: Check window.customer object
    if (window.customer && window.customer.first_name) {
      console.log('Using window.customer name:', window.customer.first_name);
      return window.customer.first_name;
    }
    
    // PRIORITY 3: Check global customerName
    if (window.customerName && window.customerName !== 'undefined' && !window.customerName.includes('@')) {
      const name = window.customerName.split(' ')[0];
      console.log('Using window.customerName:', name);
      return name;
    }
    
    // FALLBACK: Use email prefix
    const email = window.customerEmail || window.customer?.email || '';
    if (email && email.includes('@')) {
      const nameFromEmail = email.split('@')[0]
        .replace(/[^a-zA-Z]/g, '')
        .replace(/^\w/, c => c.toUpperCase());
      console.log('Using email-derived name:', nameFromEmail);
      return nameFromEmail;
    }
    
    console.log('Using fallback name');
    return 'Friend';
  }

  extractExistingAddresses() {
    let addresses = [];
    
    console.log('=== EXTRACTING ADDRESSES ===');
    console.log('window.customer:', window.customer);
    console.log('window.customerAddresses:', window.customerAddresses);
    
    // Method 1: window.customer.addresses
    if (window.customer && Array.isArray(window.customer.addresses) && window.customer.addresses.length > 0) {
      addresses = window.customer.addresses;
      console.log('✓ Method 1 - Found', addresses.length, 'addresses in window.customer.addresses');
    }
    
    // Method 2: window.customerAddresses
    if (addresses.length === 0 && typeof window.customerAddresses !== 'undefined' && Array.isArray(window.customerAddresses)) {
      addresses = window.customerAddresses;
      console.log('✓ Method 2 - Found', addresses.length, 'addresses in window.customerAddresses');
    }
    
    // Method 3: Check for default address
    if (addresses.length === 0 && window.customer && window.customer.default_address) {
      addresses = [window.customer.default_address];
      console.log('✓ Method 3 - Found default address');
    }
    
    // Method 4: Parse from DOM
    if (addresses.length === 0) {
      console.log('Trying Method 4 - DOM parsing');
      const addressElements = document.querySelectorAll('address, .address, [data-customer-address]');
      console.log('Found', addressElements.length, 'address elements in DOM');
      
      addressElements.forEach((el, index) => {
        if (el.closest('.dashboard-container')) return; // Skip our own elements
        
        const text = el.textContent.trim();
        if (text && text.length > 10) {
          const isDefault = el.classList.contains('default') || 
                           el.classList.contains('default-address') ||
                           el.hasAttribute('data-default') ||
                           text.toLowerCase().includes('default');
          
          addresses.push({
            id: el.getAttribute('data-address-id') || `dom_addr_${index}`,
            text: text,
            html: el.innerHTML,
            default: isDefault
          });
        }
      });
      
      if (addresses.length > 0) {
        console.log('✓ Method 4 - Found', addresses.length, 'addresses from DOM');
      }
    }
    
    if (addresses.length === 0) {
      console.log('❌ NO ADDRESSES FOUND IN ANY SOURCE');
      console.log('Checked: window.customer.addresses, window.customerAddresses, window.customer.default_address, DOM elements');
      return '';
    }
    
    console.log('=== TOTAL ADDRESSES FOUND:', addresses.length, '===');
    addresses.forEach((addr, i) => {
      console.log(`Address ${i + 1}:`, {
        default: !!(addr.default || addr.default_address),
        id: addr.id,
        hasName: !!(addr.first_name || addr.last_name),
        address: addr
      });
    });
    
    return addresses.map((addr, index) => {
      const isDefault = !!(addr.default || addr.default_address);
      
      let addressDisplay = '';
      
      if (addr.first_name || addr.last_name) {
        addressDisplay = `
          <strong>${addr.first_name || ''} ${addr.last_name || ''}</strong><br>
          ${addr.company ? `${addr.company}<br>` : ''}
          ${addr.address1 || ''}<br>
          ${addr.address2 ? `${addr.address2}<br>` : ''}
          ${addr.city || ''}, ${addr.province || addr.province_code || ''} ${addr.zip || ''}<br>
          ${addr.country || addr.country_code || ''}<br>
          ${addr.phone ? `Phone: ${addr.phone}` : ''}
        `.trim();
      } else if (addr.html) {
        addressDisplay = addr.html;
      } else if (addr.text) {
        addressDisplay = addr.text;
      } else {
        addressDisplay = 'Address information';
      }
      
      return `
        <div class="address-card">
          ${isDefault ? '<div class="address-default-badge">Default</div>' : ''}
          <div class="address-content">${addressDisplay}</div>
          <div class="address-actions">
            <button class="address-btn edit" onclick="accountDashboard.editAddress('${addr.id || index}')">Edit</button>
            ${!isDefault ? `<button class="address-btn delete" onclick="accountDashboard.deleteAddress('${addr.id || index}')">Delete</button>` : ''}
          </div>
        </div>
      `;
    }).join('');
  }

  extractExistingOrders() {
    console.log('=== EXTRACTING ORDERS ===');
    
    const orderData = [];
    
    // Try to find orders table
    const tables = document.querySelectorAll('table');
    console.log('Found', tables.length, 'tables on page');
    
    tables.forEach((table, tableIndex) => {
      if (table.closest('.dashboard-container')) {
        console.log('Skipping table', tableIndex, '- inside dashboard');
        return; // Skip our own table
      }
      
      console.log('Checking table', tableIndex);
      const rows = Array.from(table.querySelectorAll('tbody tr, tr'));
      console.log('  - Has', rows.length, 'rows');
      
      rows.slice(0, 6).forEach((row, rowIndex) => {
        const cells = row.cells;
        if (!cells || cells.length < 2) return;
        
        let orderNum = cells[0].textContent.trim();
        orderNum = orderNum.replace(/^#/, '').trim();
        
        const date = cells[1] ? cells[1].textContent.trim() : '';
        const status = cells[2] ? cells[2].textContent.trim().toLowerCase() : 'pending';
        const total = cells[3] ? cells[3].textContent.trim() : '';
        
        console.log('  Row', rowIndex, ':', { orderNum, date, status, total });
        
        if (orderNum && orderNum.length > 0 && !orderNum.toLowerCase().includes('order') && orderNum !== 'Order') {
          orderData.push({ orderNum, date, status, total });
          console.log('  ✓ Added order:', orderNum);
        }
      });
    });
    
    // Try alternate method - look for order links
    if (orderData.length === 0) {
      console.log('No orders from tables, trying links...');
      const orderLinks = document.querySelectorAll('a[href*="/account/orders/"], a[href*="/orders/"]');
      console.log('Found', orderLinks.length, 'order links');
      
      orderLinks.forEach((link, index) => {
        if (link.closest('.dashboard-container')) return;
        
        const href = link.getAttribute('href');
        const match = href.match(/orders\/(\d+)/);
        if (match) {
          const orderNum = match[1];
          const parent = link.closest('tr, .order, [data-order]');
          const dateMatch = parent ? parent.textContent.match(/\w+ \d+, \d{4}/) : null;
          
          orderData.push({
            orderNum: orderNum,
            date: dateMatch ? dateMatch[0] : 'Recent',
            status: 'fulfilled',
            total: ''
          });
          console.log('✓ Found order from link:', orderNum);
        }
      });
    }
    
    console.log('=== TOTAL ORDERS FOUND:', orderData.length, '===');
    orderData.forEach(order => console.log('  -', order));
    
    if (orderData.length === 0) {
      console.log('❌ NO ORDERS FOUND');
      return '';
    }
    
    return `
      <div class="orders-list">
        ${orderData.slice(0, 5).map(order => `
          <div class="order-item">
            <div class="order-header">
              <a href="/account/orders/${order.orderNum}" class="order-number">#${order.orderNum}</a>
              <span class="order-status ${order.status}">${order.status}</span>
            </div>
            <div class="order-details">
              <span class="order-date">${order.date}</span>
              ${order.total ? `<span class="order-total">${order.total}</span>` : ''}
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  hasOrders() {
    const tables = document.querySelectorAll('table');
    for (const table of tables) {
      if (table.closest('.dashboard-container')) continue;
      if (table.rows && table.rows.length > 1) {
        console.log('✓ Has orders (found table with rows)');
        return true;
      }
    }
    
    const orderLinks = document.querySelectorAll('a[href*="/account/orders/"], a[href*="/orders/"]');
    if (orderLinks.length > 0) {
      console.log('✓ Has orders (found order links)');
      return true;
    }
    
    console.log('❌ No orders found');
    return false;
  }

  hasAddresses() {
    if (window.customer && window.customer.addresses && window.customer.addresses.length > 0) {
      console.log('✓ Has addresses (window.customer.addresses)');
      return true;
    }
    if (window.customer && window.customer.default_address) {
      console.log('✓ Has addresses (default_address)');
      return true;
    }
    if (window.customerAddresses && window.customerAddresses.length > 0) {
      console.log('✓ Has addresses (window.customerAddresses)');
      return true;
    }
    
    const addressElements = document.querySelectorAll('address, .address, [data-customer-address]');
    for (const el of addressElements) {
      if (el.closest('.dashboard-container')) continue;
      if (el.textContent.trim().length > 10) {
        console.log('✓ Has addresses (DOM elements)');
        return true;
      }
    }
    
    console.log('❌ No addresses found');
    return false;
  }

  renderHorses() {
    if (this.horses.length === 0) {
      return `
        <div class="empty-state">
          <p>No horses added yet</p>
          <button onclick="accountDashboard.showAddHorseModal()" class="btn-primary">Add Your First Horse</button>
        </div>
      `;
    }

    return `
      <div class="horses-grid-compact">
        ${this.horses.map(horse => `
          <div class="horse-card-compact">
            <div class="horse-image-compact">
              ${horse.image ? 
                `<img src="${horse.image}" alt="${horse.name}">` : 
                `🐴`
              }
            </div>
            <div class="horse-info-compact">
              <h4>${horse.name}</h4>
              <p>${this.wishlists[horse.id]?.length || 0} items</p>
            </div>
            <div class="horse-actions-compact">
              <button onclick="accountDashboard.viewHorseWishlist('${horse.id}')">View</button>
              <button onclick="accountDashboard.editHorse('${horse.id}')">Edit</button>
            </div>
          </div>
        `).join('')}
      </div>
      <button onclick="accountDashboard.showAddHorseModal()" class="btn-primary" style="margin-top: 12px; width: 100%;">+ Add Horse</button>
    `;
  }

  renderWishlist() {
    const allItems = Object.values(this.wishlists).flat();
    const previewItems = allItems.slice(0, 4);
    
    console.log('Rendering wishlist, total items:', allItems.length, 'preview:', previewItems.length);
    
    if (previewItems.length === 0) {
      return `
        <div class="empty-state">
          <p>No items in wishlist yet</p>
          <button onclick="window.location.href='/collections/all-products'" class="btn-primary">Start Shopping</button>
        </div>
      `;
    }

    return `
      <div class="wishlist-grid-compact">
        ${previewItems.map(handle => {
          const product = this.products[handle];
          if (!product) {
            console.log('⚠️ Product not found for handle:', handle);
            return '';
          }
          
          console.log('✓ Rendering wishlist item:', product.title);
          
          return `
            <div class="wishlist-item-card">
              <div class="wishlist-item-image">
                <img src="${product.featured_image}" alt="${product.title}" onerror="console.error('Failed to load image:', this.src)">
              </div>
              <div class="wishlist-item-info">
                <div class="wishlist-item-title">${product.title}</div>
                <div class="wishlist-item-price">$${(product.price / 100).toFixed(2)}</div>
              </div>
              <div class="wishlist-item-actions">
                <button onclick="accountDashboard.openQuickView('${handle}')" class="wishlist-action-btn">Quick View</button>
                <a href="/products/${handle}" class="wishlist-action-btn">View</a>
              </div>
            </div>
          `;
        }).join('')}
      </div>
      <div class="wishlist-view-all-container">
        <a href="/pages/my-wishlist" class="wishlist-view-all-link">View All ${allItems.length} Items →</a>
      </div>
    `;
  }

  openQuickView(handle) {
    console.log('Opening quick view for:', handle);
    
    const product = this.products[handle];
    if (!product) {
      console.error('Product not found for quick view:', handle);
      return;
    }
    
    // Try to trigger the site's existing quick view
    const quickViewButton = document.querySelector(`[data-product-handle="${handle}"] .quick-view, [data-product-id="${product.id}"] .quick-view`);
    if (quickViewButton) {
      console.log('✓ Found existing quick view button, clicking it');
      quickViewButton.click();
      return;
    }
    
    // Check if there's a global quick view function
    if (typeof window.quickView === 'function') {
      console.log('✓ Found global quickView function');
      window.quickView(product.handle);
      return;
    }
    
    // Try theme-specific quick view
    if (typeof theme !== 'undefined' && typeof theme.QuickView !== 'undefined') {
      console.log('✓ Found theme.QuickView');
      new theme.QuickView(product.handle);
      return;
    }
    
    // Fallback: Create our own modal
    console.log('No existing quick view found, creating our own modal');
    this.showProductQuickView(handle);
  }

  showProductQuickView(handle) {
    const product = this.products[handle];
    if (!product) return;
    
    console.log('Showing custom quick view modal for:', product.title);
    
    const modal = document.createElement('div');
    modal.className = 'address-modal-overlay';
    modal.innerHTML = `
      <div class="address-modal" style="max-width: 600px;">
        <div class="address-modal-header">
          <h2>${product.title}</h2>
          <button class="address-modal-close" onclick="this.closest('.address-modal-overlay').remove()">×</button>
        </div>
        <div class="address-modal-form">
          <div style="display: flex; gap: 20px; margin-bottom: 20px;">
            <div style="flex: 1;">
              <img src="${product.featured_image}" style="width: 100%; border-radius: 10px;" alt="${product.title}">
            </div>
            <div style="flex: 1;">
              <div style="font-size: 24px; font-weight: 700; color: #A67C59; margin-bottom: 15px;">
                $${(product.price / 100).toFixed(2)}
              </div>
              ${product.description ? `
                <div style="margin-bottom: 20px; color: #666; font-size: 14px; line-height: 1.6;">
                  ${product.description.substring(0, 200)}${product.description.length > 200 ? '...' : ''}
                </div>
              ` : ''}
              ${product.variants && product.variants.length > 0 ? `
                <div style="margin-bottom: 20px;">
                  <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #3B3227;">Options</label>
                  <select style="width: 100%; padding: 10px; border: 2px solid #DCCCB3; border-radius: 12px;">
                    ${product.variants.map(variant => `
                      <option value="${variant.id}" ${variant.available ? '' : 'disabled'}>
                        ${variant.title} - $${(variant.price / 100).toFixed(2)} ${variant.available ? '' : '(Sold Out)'}
                      </option>
                    `).join('')}
                  </select>
                </div>
              ` : ''}
            </div>
          </div>
          <div class="form-actions">
            <a href="/products/${handle}" class="btn-primary" style="flex: 1; text-align: center; text-decoration: none; display: flex; align-items: center; justify-content: center;">
              View Full Product
            </a>
            <button onclick="alert('Add to cart functionality coming soon!')" class="btn-secondary" style="flex: 1;">
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.remove();
    });
  }

  trackRecentlyViewed(productHandle, productTitle, productImage, productPrice) {
    if (!productHandle || !productTitle) {
      console.log('⚠️ Cannot track - missing handle or title');
      return;
    }
    
    console.log('Tracking recently viewed:', productTitle);
    
    let recentlyViewed = JSON.parse(localStorage.getItem(`recently_viewed_${this.customerId}`)) || [];
    
    // Remove if already exists
    recentlyViewed = recentlyViewed.filter(p => p.handle !== productHandle);
    
    // Add to beginning
    recentlyViewed.unshift({
      handle: productHandle,
      title: productTitle,
      image: productImage,
      price: productPrice,
      timestamp: new Date().toISOString()
    });
    
    // Keep last 100
    recentlyViewed = recentlyViewed.slice(0, 100);
    
    localStorage.setItem(`recently_viewed_${this.customerId}`, JSON.stringify(recentlyViewed));
    console.log('✓ Tracked recently viewed:', productTitle);
    console.log('✓ Total recently viewed products:', recentlyViewed.length);
  }

  renderRecentlyViewed() {
    const recentlyViewed = JSON.parse(localStorage.getItem(`recently_viewed_${this.customerId}`)) || [];
    console.log('Rendering recently viewed, found:', recentlyViewed.length, 'products');
    
    const previewItems = recentlyViewed.slice(0, 4);
    
    if (previewItems.length === 0) {
      return `
        <div class="empty-state">
          <p>No recently viewed products</p>
          <button onclick="window.location.href='/collections/all-products'" class="btn-primary">Browse Products</button>
        </div>
      `;
    }

    return `
      <div class="recently-viewed-grid">
        ${previewItems.map(product => {
          console.log('✓ Rendering recently viewed:', product.title, 'Image:', product.image);
          return `
            <a href="/products/${product.handle}" class="recently-viewed-item">
              <div class="recently-viewed-image">
                <img src="${product.image}" alt="${product.title}" onerror="console.error('Failed to load recently viewed image:', this.src); this.src='data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'60\' height=\'60\'%3E%3Crect fill=\'%23F9EBCC\' width=\'60\' height=\'60\'/%3E%3C/svg%3E'">
              </div>
              <div class="recently-viewed-info">
                <div class="recently-viewed-title">${product.title}</div>
                <div class="recently-viewed-price">$${((product.price || 0) / 100).toFixed(2)}</div>
              </div>
            </a>
          `;
        }).join('')}
      </div>
    `;
  }

  getTotalWishlistItems() {
    return Object.values(this.wishlists).flat().length;
  }

  renderAccountDetails() {
    const profile = this.customerData.profile || {};
    const firstName = profile.firstName || window.customer?.first_name || this.getCustomerFirstName();
    const lastName = profile.lastName || window.customer?.last_name || '';
    const email = profile.email || window.customerEmail || window.customer?.email || '';
    const phone = profile.phone || window.customer?.phone || '';
    
    return `
      <div class="account-details-list">
        <div class="account-detail-row">
          <span class="account-detail-label">First Name</span>
          <span class="account-detail-value">${firstName}</span>
        </div>
        <div class="account-detail-row">
          <span class="account-detail-label">Last Name</span>
          <span class="account-detail-value">${lastName || 'Not set'}</span>
        </div>
        <div class="account-detail-row">
          <span class="account-detail-label">Email</span>
          <span class="account-detail-value">${email}</span>
        </div>
        <div class="account-detail-row">
          <span class="account-detail-label">Phone</span>
          <span class="account-detail-value">${phone || 'Not set'}</span>
        </div>
      </div>
      <button onclick="accountDashboard.showProfileEditModal()" class="btn-primary" style="width: 100%; margin-top: 15px;">
        Edit Profile
      </button>
      <button onclick="accountDashboard.showChangePasswordModal()" class="btn-secondary" style="width: 100%; margin-top: 10px;">
        Change Password
      </button>
      <div style="margin-top: 20px; padding-top: 20px; border-top: 2px solid #F0F0F0;">
        <div style="margin-bottom: 10px;">
          <a href="/pages/my-wishlist" style="display: block; padding: 12px; background: #FAFAFA; border-radius: 10px; text-decoration: none; color: #3B3227; font-weight: 600; transition: all 0.3s;" onmouseover="this.style.background='#F9EBCC'" onmouseout="this.style.background='#FAFAFA'">
            💝 My Wishlist
          </a>
        </div>
        <div style="margin-bottom: 10px;">
          <a href="/account/addresses" style="display: block; padding: 12px; background: #FAFAFA; border-radius: 10px; text-decoration: none; color: #3B3227; font-weight: 600; transition: all 0.3s;" onmouseover="this.style.background='#F9EBCC'" onmouseout="this.style.background='#FAFAFA'">
            📍 Manage Addresses
          </a>
        </div>
        <div>
          <a href="/account" style="display: block; padding: 12px; background: #FAFAFA; border-radius: 10px; text-decoration: none; color: #3B3227; font-weight: 600; transition: all 0.3s;" onmouseover="this.style.background='#F9EBCC'" onmouseout="this.style.background='#FAFAFA'">
            📦 Order History
          </a>
        </div>
      </div>
    `;
  }

  showProfileEditModal() {
    const profile = this.customerData.profile || {};
    const firstName = profile.firstName || window.customer?.first_name || this.getCustomerFirstName();
    const lastName = profile.lastName || window.customer?.last_name || '';
    const email = profile.email || window.customerEmail || window.customer?.email || '';
    const phone = profile.phone || window.customer?.phone || '';
    
    const modal = document.createElement('div');
    modal.className = 'address-modal-overlay';
    modal.innerHTML = `
      <div class="address-modal">
        <div class="address-modal-header">
          <h2>Edit Profile Information</h2>
          <button class="address-modal-close" onclick="this.closest('.address-modal-overlay').remove()">×</button>
        </div>
        <form class="address-modal-form" id="profile-edit-form">
          <div class="form-row">
            <div class="form-group">
              <label for="profile_first_name">First Name *</label>
              <input type="text" id="profile_first_name" value="${firstName}" required>
            </div>
            <div class="form-group">
              <label for="profile_last_name">Last Name *</label>
              <input type="text" id="profile_last_name" value="${lastName}" required>
            </div>
          </div>
          <div class="form-group">
            <label for="profile_email">Email *</label>
            <input type="email" id="profile_email" value="${email}" required>
          </div>
          <div class="form-group">
            <label for="profile_phone">Phone</label>
            <input type="tel" id="profile_phone" value="${phone}">
          </div>
          <div class="form-actions">
            <button type="button" class="btn-secondary" onclick="this.closest('.address-modal-overlay').remove()">Cancel</button>
            <button type="button" class="btn-primary" onclick="accountDashboard.saveProfile()">Save Changes</button>
          </div>
        </form>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.remove();
    });
  }

  saveProfile() {
    const firstName = document.getElementById('profile_first_name').value;
    const lastName = document.getElementById('profile_last_name').value;
    const email = document.getElementById('profile_email').value;
    const phone = document.getElementById('profile_phone').value;

    this.customerData.profile = {
      ...this.customerData.profile,
      firstName,
      lastName,
      email,
      phone
    };
    
    this.saveCustomerData();

    if (window.customer) {
      window.customer.first_name = firstName;
      window.customer.last_name = lastName;
      window.customer.email = email;
      window.customer.phone = phone;
    }

    document.querySelector('.address-modal-overlay')?.remove();
    console.log('✓ Profile saved successfully');
    
    // Reload the page to show updated info
    location.reload();
  }

  showChangePasswordModal() {
    const email = window.customerEmail || window.customer?.email || '';
    
    const modal = document.createElement('div');
    modal.className = 'address-modal-overlay';
    modal.innerHTML = `
      <div class="address-modal" style="max-width: 500px;">
        <div class="address-modal-header">
          <h2>Change Your Password</h2>
          <button class="address-modal-close" onclick="this.closest('.address-modal-overlay').remove()">×</button>
        </div>
        <div class="address-modal-form">
          <p style="color: #666; margin-bottom: 20px; line-height: 1.6;">
            To change your password, we'll send you a reset link via email. Click the link in the email to set a new password.
          </p>
          
          <div class="form-group">
            <label>Your Email Address</label>
            <input type="email" value="${email}" readonly style="background: #f5f5f5; color: #666;">
          </div>
          
          <div class="form-actions">
            <button type="button" class="btn-secondary" onclick="this.closest('.address-modal-overlay').remove()">Cancel</button>
            <button type="button" class="btn-primary" onclick="accountDashboard.requestPasswordReset()">Send Reset Link</button>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.remove();
    });
  }

  requestPasswordReset() {
    console.log('Requesting password reset...');
    
    const email = window.customerEmail || window.customer?.email || '';
    
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/account/recover';
    
    const emailInput = document.createElement('input');
    emailInput.type = 'hidden';
    emailInput.name = 'email';
    emailInput.value = email;
    
    form.appendChild(emailInput);
    document.body.appendChild(form);
    
    console.log('✓ Submitting password reset form for:', email);
    form.submit();
  }

  addAddress() {
    this.showAddressModal();
  }

  editAddress(addressId) {
    this.showAddressModal(addressId);
  }

  deleteAddress(addressId) {
    if (!confirm('Are you sure you want to delete this address?')) return;
    
    console.log('Deleting address:', addressId);
    
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `/account/addresses/${addressId}`;
    
    const deleteInput = document.createElement('input');
    deleteInput.type = 'hidden';
    deleteInput.name = '_method';
    deleteInput.value = 'delete';
    
    form.appendChild(deleteInput);
    document.body.appendChild(form);
    
    console.log('✓ Submitting delete form');
    form.submit();
  }

  showAddressModal(addressId = null) {
    let address = {};
    if (addressId && window.customer && window.customer.addresses) {
      address = window.customer.addresses.find(a => a.id == addressId) || {};
      console.log('Editing address:', address);
    } else {
      console.log('Adding new address');
    }

    const isEdit = !!addressId;
    
    const modal = document.createElement('div');
    modal.className = 'address-modal-overlay';
    modal.innerHTML = `
      <div class="address-modal">
        <div class="address-modal-header">
          <h2>${isEdit ? 'Edit Address' : 'Add New Address'}</h2>
          <button class="address-modal-close" onclick="this.closest('.address-modal-overlay').remove()">×</button>
        </div>
        <form class="address-modal-form" id="address-form" method="post" action="/account/addresses${isEdit ? `/${addressId}` : ''}">
          ${isEdit ? '<input type="hidden" name="_method" value="put">' : ''}
          
          <div class="form-row">
            <div class="form-group">
              <label for="address_first_name">First Name *</label>
              <input type="text" id="address_first_name" name="address[first_name]" value="${address.first_name || ''}" required>
            </div>
            <div class="form-group">
              <label for="address_last_name">Last Name *</label>
              <input type="text" id="address_last_name" name="address[last_name]" value="${address.last_name || ''}" required>
            </div>
          </div>

          <div class="form-group">
            <label for="address_company">Company</label>
            <input type="text" id="address_company" name="address[company]" value="${address.company || ''}">
          </div>

          <div class="form-group">
            <label for="address_address1">Address *</label>
            <input type="text" id="address_address1" name="address[address1]" value="${address.address1 || ''}" required>
          </div>

          <div class="form-group">
            <label for="address_address2">Apartment, suite, etc.</label>
            <input type="text" id="address_address2" name="address[address2]" value="${address.address2 || ''}">
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="address_city">City *</label>
              <input type="text" id="address_city" name="address[city]" value="${address.city || ''}" required>
            </div>
            <div class="form-group">
              <label for="address_province">State/Province *</label>
              <input type="text" id="address_province" name="address[province]" value="${address.province || ''}" required>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="address_zip">Postal/Zip Code *</label>
              <input type="text" id="address_zip" name="address[zip]" value="${address.zip || ''}" required>
            </div>
            <div class="form-group">
              <label for="address_country">Country *</label>
              <select id="address_country" name="address[country]" required>
                <option value="">Select Country</option>
                <option value="Australia" ${address.country === 'Australia' ? 'selected' : ''}>Australia</option>
                <option value="United States" ${address.country === 'United States' ? 'selected' : ''}>United States</option>
                <option value="Canada" ${address.country === 'Canada' ? 'selected' : ''}>Canada</option>
                <option value="United Kingdom" ${address.country === 'United Kingdom' ? 'selected' : ''}>United Kingdom</option>
                <option value="New Zealand" ${address.country === 'New Zealand' ? 'selected' : ''}>New Zealand</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label for="address_phone">Phone</label>
            <input type="tel" id="address_phone" name="address[phone]" value="${address.phone || ''}">
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" name="address[default]" value="1" ${address.default ? 'checked' : ''}>
              <span>Set as default address</span>
            </label>
          </div>

          <div class="form-actions">
            <button type="button" class="btn-secondary" onclick="this.closest('.address-modal-overlay').remove()">Cancel</button>
            <button type="submit" class="btn-primary">${isEdit ? 'Update Address' : 'Add Address'}</button>
          </div>
        </form>
      </div>
    `;

    document.body.appendChild(modal);

    const form = document.getElementById('address-form');
    form.addEventListener('submit', (e) => {
      console.log('✓ Submitting address form');
      // Let the form submit naturally to Shopify
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.remove();
    });
  }

  transformAccountPage() {
    const container = document.querySelector('.customer.account, main[role="main"]');
    if (!container) {
      console.log('❌ No account container found');
      return;
    }

    const hasOrders = this.hasOrders();
    const hasAddresses = this.hasAddresses();
    const firstName = this.getCustomerFirstName();

    console.log('=== TRANSFORMING ACCOUNT PAGE ===');
    console.log('Has orders:', hasOrders);
    console.log('Has addresses:', hasAddresses);
    console.log('First name:', firstName);

    container.innerHTML = `
      <div class="dashboard-container">
        <div class="dashboard-header">
          <div class="dashboard-welcome">
            <div class="avatar-section">
              <div class="avatar-container">
                ${this.customerData.avatar ? 
                  `<img src="${this.customerData.avatar}" alt="Profile" class="avatar-image">` :
                  `<div class="avatar-placeholder">
                    <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>`
                }
              </div>
              <span class="avatar-upload-text" onclick="accountDashboard.changeAvatar()">
                ${this.customerData.avatar ? 'Change image' : 'Upload image'}
              </span>
              <input type="file" id="avatar-upload" accept="image/*" style="display: none;">
            </div>
            <div class="welcome-text">
              <h1 class="customer-greeting">Welcome back, ${firstName}!</h1>
              <p class="customer-email">${window.customerEmail || ''}</p>
              <div class="customer-badges">
                <span class="badge badge-member">Member since ${this.getMembershipDuration()}</span>
                ${this.getCustomerBadges()}
              </div>
            </div>
          </div>
          <div class="quick-actions">
            <button onclick="window.location.href='/collections/all-products'" class="action-button shop">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 3h2l.4 2m0 0h13.2a1 1 0 0 1 .9 1.4l-1.7 3a1 1 0 0 1-.9.6H7.3m0 0L6 11m1.3 0L9 13h11"/>
                <circle cx="9" cy="20" r="1"/>
                <circle cx="20" cy="20" r="1"/>
              </svg>
              Shop Now
            </button>
            <button onclick="window.location.href='/pages/my-wishlist'" class="action-button wishlist">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              Wishlist
            </button>
            <button onclick="window.location.href='/account/logout'" class="action-button logout">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <path d="M16 17l5-5-5-5"/>
                <path d="M21 12H9"/>
              </svg>
              Log Out
            </button>
          </div>
        </div>

        <div class="dashboard-stats">
          <div class="stat-card">
            <div class="stat-icon orders">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.6-1.3 2.4c-.2.4-.3.8-.3 1.2 0 1.1.9 2 2 2h12v-2H7.4c-.1 0-.2-.1-.2-.2l.1-.2L9 11h7c.6 0 1.1-.3 1.4-.8l3.6-6.5c.1-.1.1-.3.1-.4 0-.6-.5-1-1-1H5.2l-.9-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
              </svg>
            </div>
            <div class="stat-content">
              <div class="stat-number" id="total-orders">0</div>
              <div class="stat-label">Total Orders</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon spent">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <div class="stat-content">
              <div class="stat-number" id="total-spent">$0.00</div>
              <div class="stat-label">Total Spent</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon horses">🐴</div>
            <div class="stat-content">
              <div class="stat-number">${this.horses.length}</div>
              <div class="stat-label">${this.horses.length === 1 ? 'Horse' : 'Horses'}</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon wishlist-items">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </div>
            <div class="stat-content">
              <div class="stat-number">${this.getTotalWishlistItems()}</div>
              <div class="stat-label">Wishlist Items</div>
            </div>
          </div>
        </div>

        <div class="dashboard-grid">
          <div class="dashboard-section orders-section">
            <div class="dashboard-section-header"><h2>Recent Orders</h2></div>
            <div class="section-content">
              ${hasOrders ? this.extractExistingOrders() : `
                <div class="empty-state">
                  <p>No orders yet</p>
                  <button onclick="window.location.href='/collections/all-products'" class="btn-primary">Start Shopping</button>
                </div>
              `}
            </div>
          </div>

          <div class="dashboard-section wishlist-section">
            <div class="dashboard-section-header"><h2>My Wishlist</h2></div>
            <div class="section-content">${this.renderWishlist()}</div>
          </div>

          <div class="dashboard-section horses-section">
            <div class="dashboard-section-header"><h2>My Horses</h2></div>
            <div class="section-content">${this.renderHorses()}</div>
          </div>

          <div class="dashboard-section recently-viewed-section">
            <div class="dashboard-section-header"><h2>Recently Viewed</h2></div>
            <div class="section-content">${this.renderRecentlyViewed()}</div>
          </div>

          <div class="dashboard-section addresses-section">
            <div class="dashboard-section-header"><h2>Addresses</h2></div>
            <div class="section-content">
              ${hasAddresses ? `
                <div class="addresses-list">${this.extractExistingAddresses()}</div>
                <button onclick="accountDashboard.addAddress()" class="btn-primary" style="margin-top: 15px; width: 100%;">+ Add Address</button>
              ` : `
                <div class="empty-state">
                  <p>No saved addresses</p>
                  <button onclick="accountDashboard.addAddress()" class="btn-primary">Add Address</button>
                </div>
              `}
            </div>
          </div>

          <div class="dashboard-section settings-section">
            <div class="dashboard-section-header"><h2>Account Settings</h2></div>
            <div class="section-content">
              ${this.renderAccountDetails()}
            </div>
          </div>
        </div>
      </div>
    `;

    console.log('✓ Account page transformed successfully');
    this.setupAvatarUpload();
    this.updateHeaderAvatar();
  }

  changeAvatar() {
    document.getElementById('avatar-upload')?.click();
  }

  setupAvatarUpload() {
    const fileInput = document.getElementById('avatar-upload');
    if (fileInput) {
      fileInput.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.customerData.avatar = e.target.result;
            this.saveCustomerData();
            
            const avatarContainer = document.querySelector('.avatar-container');
            if (avatarContainer) {
              avatarContainer.innerHTML = `<img src="${e.target.result}" alt="Profile" class="avatar-image">`;
            }
            
            const uploadText = document.querySelector('.avatar-upload-text');
            if (uploadText) {
              uploadText.textContent = 'Change image';
            }
            
            this.updateHeaderAvatar();
            console.log('✓ Avatar updated successfully');
          };
          reader.readAsDataURL(file);
        }
      };
    }
  }

  updateHeaderAvatar() {
    if (!this.customerData.avatar) return;
    
    setTimeout(() => {
      const accountIcons = document.querySelectorAll('.header__icon--account, a[href*="/account"]');
      accountIcons.forEach(iconElement => {
        const icon = iconElement.querySelector('svg')?.parentElement || iconElement;
        icon.style.cssText = 'width: 32px !important; height: 32px !important; border-radius: 50% !important; overflow: hidden !important; display: flex !important; align-items: center !important; justify-content: center !important; border: 2px solid #DCCCB3 !important; padding: 0 !important;';
        icon.innerHTML = `<img src="${this.customerData.avatar}" style="width: 100% !important; height: 100% !important; object-fit: cover !important; border-radius: 50% !important;" alt="Account">`;
      });
      console.log('✓ Header avatar updated');
    }, 500);
  }

  loadDynamicContent() {
    console.log('=== LOADING DYNAMIC CONTENT ===');
    
    // Count orders
    const orderItems = document.querySelectorAll('.order-item');
    const orderCount = orderItems.length;
    document.getElementById('total-orders').textContent = orderCount;
    console.log('✓ Order count:', orderCount);
    
    // Calculate total spent
    let totalSpent = 0;
    document.querySelectorAll('.order-total').forEach(el => {
      const text = el.textContent.trim();
      const match = text.match(/\$?([0-9,]+\.?\d*)/);
      if (match) {
        const amount = parseFloat(match[1].replace(/,/g, ''));
        totalSpent += amount;
        console.log('  Found order total:', amount);
      }
    });
    
    document.getElementById('total-spent').textContent = `$${totalSpent.toFixed(2)}`;
    console.log('✓ Total spent:', totalSpent);
  }

  getMembershipDuration() {
    const joinDate = new Date(this.customerData.joinDate);
    const now = new Date();
    const days = Math.floor((now - joinDate) / (1000 * 60 * 60 * 24));
    
    if (days < 30) return 'this month';
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months > 1 ? 's' : ''}`;
    const years = Math.floor(months / 12);
    return `${years} year${years > 1 ? 's' : ''}`;
  }

  getCustomerBadges() {
    let badges = '';
    if (this.horses.length >= 5) {
      badges += '<span class="badge badge-gold">🏆 Stable Owner</span>';
    } else if (this.horses.length >= 1) {
      badges += '<span class="badge badge-blue">🐴 Horse Owner</span>';
    }
    return badges;
  }

  initActivityTracking() {
    console.log('=== INITIALIZING ACTIVITY TRACKING ===');
    
    if (window.location.pathname.includes('/products/')) {
      console.log('On product page, setting up tracking...');
      setTimeout(() => {
        const handle = window.location.pathname.split('/products/')[1]?.split('?')[0];
        if (!handle) {
          console.log('⚠️ Could not extract product handle from URL');
          return;
        }
        
        const title = document.querySelector('.product-title, .product__title, h1')?.textContent?.trim() || '';
        const image = document.querySelector('.product-image img, .product__media img, .product img')?.src || '';
        const priceEl = document.querySelector('.price, .product-price, [class*="price"]');
        const priceText = priceEl?.textContent?.trim() || '';
        const priceMatch = priceText.match(/\$?([0-9,]+\.?\d*)/);
        const price = priceMatch ? parseFloat(priceMatch[1].replace(/,/g, '')) * 100 : 0;
        
        console.log('Extracted product info:', { handle, title, image, price });
        
        if (title && handle) {
          this.trackRecentlyViewed(handle, title, image, price);
        } else {
          console.log('⚠️ Missing title or handle, cannot track');
        }
      }, 1500);
    }
  }

  setupInteractions() {
    // Additional interaction setup if needed
  }

  showAddHorseModal() {
    if (window.horseWishlist) {
      window.horseWishlist.openAddHorse(null);
    } else {
      console.log('⚠️ horseWishlist not available');
    }
  }

  editHorse(horseId) {
    const horse = this.horses.find(h => h.id === horseId);
    if (horse && window.horseWishlist) {
      window.horseWishlist.openAddHorse(horse);
    } else {
      console.log('⚠️ horseWishlist not available');
    }
  }

  viewHorseWishlist(horseId) {
    window.location.href = `/pages/my-wishlist?horse=${horseId}`;
  }
}

// Initialize dashboard if on account page
console.log('=== SCRIPT LOADED ===');
console.log('Current path:', window.location.pathname);

if (window.location.pathname.includes('/account') && !window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
  console.log('✓ On account page, initializing dashboard...');
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      console.log('✓ DOM loaded, creating dashboard instance');
      window.accountDashboard = new AccountDashboard();
    });
  } else {
    console.log('✓ DOM already loaded, creating dashboard instance');
    window.accountDashboard = new AccountDashboard();
  }
} else {
  console.log('Not on account page, skipping dashboard initialization');
}

// Track product views on product pages (works even when not logged in to account page)
if (window.location.pathname.includes('/products/') && (window.customerId || window.customer?.id)) {
  console.log('✓ On product page, setting up recently viewed tracking...');
  
  setTimeout(() => {
    const customerId = window.customerId || window.customer?.id;
    const handle = window.location.pathname.split('/products/')[1]?.split('?')[0];
    if (!handle) {
      console.log('⚠️ Could not extract product handle');
      return;
    }
    
    const title = document.querySelector('.product-title, .product__title, h1')?.textContent?.trim() || '';
    const image = document.querySelector('.product-image img, .product__media img, .product img')?.src || '';
    const priceEl = document.querySelector('.price, .product-price, [class*="price"]');
    const priceText = priceEl?.textContent?.trim() || '';
    const priceMatch = priceText.match(/\$?([0-9,]+\.?\d*)/);
    const price = priceMatch ? parseFloat(priceMatch[1].replace(/,/g, '')) * 100 : 0;
    
    console.log('Product page tracking:', { customerId, handle, title, image, price });
    
    if (title && handle && customerId) {
      let recentlyViewed = JSON.parse(localStorage.getItem(`recently_viewed_${customerId}`)) || [];
      recentlyViewed = recentlyViewed.filter(p => p.handle !== handle);
      recentlyViewed.unshift({
        handle: handle,
        title: title,
        image: image,
        price: price,
        timestamp: new Date().toISOString()
      });
      recentlyViewed = recentlyViewed.slice(0, 100);
      localStorage.setItem(`recently_viewed_${customerId}`, JSON.stringify(recentlyViewed));
      console.log('✓ Product tracked in recently viewed:', title);
    } else {
      console.log('⚠️ Missing required data for tracking');
    }
  }, 1500);
}

console.log('=== SCRIPT INITIALIZATION COMPLETE ===');